import type { CustomerAuditLogDetail, Dictionary } from '@/types'
import { generateQueryString } from '@/utils/helpers'
import { baseApi } from '../baseApi'

export const customerManagementApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCustomerDashboard: builder.query<Dictionary, Record<string, any>>({
      query: () => ({
        url: `/customer-dashboard/overview`,
        method: 'GET',
      }),
      providesTags: ['Customers'],
      transformResponse: (response: any) => response.data,
    }),
    getCustomerDashboardStats: builder.query<Dictionary, Record<string, any>>({
      query: () => ({
        url: `/customer-dashboard/statistics`,
        method: 'GET',
      }),
      providesTags: ['Customers'],
      transformResponse: (response: any) => response.data,
    }),
    getCustomerList: builder.query<Dictionary, Record<string, any>>({
      query: (param) => ({
        url: `/customers${generateQueryString(param)}`,
        method: 'GET',
      }),
      providesTags: ['Customers'],
      transformResponse: (response: any) => response.data,
    }),
    getCustomerDetails: builder.query<Dictionary, Record<string, any>>({
      query: ({ id }) => ({
        url: `/customers/${id}`,
        method: 'GET',
      }),
      providesTags: ['customers-details'],
      transformResponse: (response: any) => response.data,
    }),
    suspendCustomer: builder.mutation<
      Dictionary,
      { id: string; duration: string; reason: string }
    >({
      query: ({ id, duration, reason }) => ({
        url: `/customers/${id}/suspend`,
        method: 'POST',
        body: { duration, reason },
      }),
      invalidatesTags: ['customers-details'],
    }),
    deactivateCustomer: builder.mutation<Dictionary, { id: string; reason: string }>(
      {
        query: ({ id, reason }) => ({
          url: `/customers/${id}/deactivate`,
          method: 'POST',
          body: { reason },
        }),
        invalidatesTags: ['customers-details'],
      },
    ),
    reactivateCustomer: builder.mutation<Dictionary, { id: string }>({
      query: ({ id }) => ({
        url: `/customers/${id}/reactivate`,
        method: 'POST',
      }),
      invalidatesTags: ['customers-details'],
    }),
    getCustomerAuditLogs: builder.query<Dictionary, Record<string, any>>({
      query: ({ id, ...param }) => ({
        url: `/customers/${id}/audit-logs${generateQueryString(param)}`,
        method: 'GET',
      }),
      providesTags: ['customers-audit-logs'],
      transformResponse: (response: any) => response.data,
    }),
    getCustomerAuditLog: builder.query<
      CustomerAuditLogDetail,
      { customerId: string; auditLogId: string | number }
    >({
      query: ({ customerId, auditLogId }) => ({
        url: `/customers/${customerId}/audit-logs/${auditLogId}`,
        method: 'GET',
      }),
      providesTags: ['customers-audit-logs'],
      transformResponse: (response: any) => {
        const d = response?.data
        if (d == null || typeof d !== 'object') return d
        if (d.log != null && typeof d.log === 'object')
          return d.log as CustomerAuditLogDetail
        if (d.audit_log != null && typeof d.audit_log === 'object')
          return d.audit_log as CustomerAuditLogDetail
        if ('id' in d && ('action_type' in d || 'category' in d))
          return d as CustomerAuditLogDetail
        return d as CustomerAuditLogDetail
      },
    }),
    exportCustomerAuditLogs: builder.mutation<
      Blob,
      { id: string } & Record<string, unknown>
    >({
      query: ({ id, ...params }) => ({
        url: `/customers/${id}/audit-logs/export${generateQueryString({
          ...params,
          format: 'csv',
        })}`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),
  }),
})

export const {
  useGetCustomerDashboardQuery,
  useGetCustomerDashboardStatsQuery,
  useGetCustomerListQuery,
  useGetCustomerDetailsQuery,
  useSuspendCustomerMutation,
  useDeactivateCustomerMutation,
  useReactivateCustomerMutation,
  useGetCustomerAuditLogsQuery,
  useGetCustomerAuditLogQuery,
  useExportCustomerAuditLogsMutation,
} = customerManagementApi
